'use client';

import { supabase } from '@/integrations/supabase/client';
import { messagingApi } from '@/api/messaging';
import { useCallback, useEffect, useState, useRef } from 'react';
import { updateAllMessagesStatus } from '@/lib/update-message-status';
import {
  storeMessageStatus,
  storeAllMessageStatus,
} from '@/lib/store-message-status';
import { emailAPI } from '@/services/email/email-api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import connectionManager from '@/services/realtime-connection-manager';
import {
  getBulkCachedAttachments,
  cacheBulkAttachments,
  clearExpiredCache,
} from '@/lib/attachment-cache';

// Debug logging (set to true for development debugging)
const isDev = true;

const devLog = (...args: any[]) => {
  if (isDev) console.log('[Chat]', ...args);
};

const devError = (...args: any[]) => {
  if (isDev) console.error('[Chat]', ...args);
};

const devWarn = (...args: any[]) => {
  if (isDev) console.warn('[Chat]', ...args);
};

const devInfo = (...args: any[]) => {
  if (isDev) console.info('[Chat]', ...args);
};

const devTable = (data: any) => {
  if (isDev) console.table(data);
};

interface UseBidirectionalChatProps {
  roomName: string;
  username: string;
  entityId: string;
  entityRole: 'client' | 'filmmaker';
}

export interface MessageReaction {
  id: string;
  emoji: string;
  entityId: string;
  entityRole: 'client' | 'filmmaker';
  createdAt: string;
}

export interface ChatMessage {
  id: string;
  content: string;
  createdAt: string;
  user: {
    id: string;
    name: string;
  };
  entityId?: string;
  entityRole?: string;
  status?: 'sent' | 'delivered' | 'read';
  hasAttachments?: boolean;
  attachmentCount?: number;
  messageType?: 'text' | 'media' | 'mixed' | 'voice' | 'location';
  attachments?: any[]; // For passing attachment data directly
  // New fields for edit/delete/reactions
  editedAt?: string | null;
  deletedAt?: string | null;
  originalContent?: string | null;
  reactions?: MessageReaction[];
  isPinned?: boolean;
}

const EVENT_MESSAGE_TYPE = 'message';
const EVENT_STATUS_TYPE = 'status';
const EVENT_PRESENCE_TYPE = 'presence';

// Helper function to send message email notifications
async function sendMessageEmailNotification(
  messageContent: string,
  roomName: string,
  senderEntityId: string,
  senderEntityRole: 'client' | 'filmmaker',
  senderName: string
) {
  try {
    // Extract recipient entity ID from room name
    const roomParts = roomName.split('_');
    if (roomParts.length !== 5 || roomParts[0] !== 'chat') {
      devError(
        'Invalid room name format for email notification:',
        roomName
      );
      return;
    }

    const clientId = roomParts[2];
    const filmmakerId = roomParts[4];

    // Determine recipient entity ID and role
    let recipientEntityId: string;
    let recipientRole: 'client' | 'filmmaker';

    if (senderEntityRole === 'client' && senderEntityId === clientId) {
      recipientEntityId = filmmakerId;
      recipientRole = 'filmmaker';
    } else if (
      senderEntityRole === 'filmmaker' &&
      senderEntityId === filmmakerId
    ) {
      recipientEntityId = clientId;
      recipientRole = 'client';
    } else {
      devError('Sender not found in room participants');
      return;
    }

    // Get recipient email based on their entity ID and role
    let recipientEmail: string;

    try {
      const emailData = await messagingApi.getUserEmail(recipientEntityId, recipientRole);
      recipientEmail = emailData.email;
    } catch (error) {
      devError('Failed to get user email for notification:', error);
      return;
    }

    // Get message preview (first 100 characters)
    const messagePreview =
      messageContent.length > 100
        ? messageContent.substring(0, 100) + '...'
        : messageContent;

    // Send email notification using the emailAPI
    await emailAPI.sendMessageNotification(recipientEmail, {
      senderName: senderName,
      messagePreview: messagePreview,
      context: undefined, // Could be enhanced with job/project context in the future
      recipientRole: recipientRole, // Add recipient role for correct dashboard URL
      // Navigation IDs for clickable notifications
      room_name: roomName,
      sender_id: senderEntityId,
      sender_role: senderEntityRole,
    });
  } catch (error) {
    devError('Error sending message email notification:', error);
    throw error;
  }
}

export interface TypingUser {
  id: string;
  name: string;
  role: string;
}

export function useBidirectionalChat({
  roomName,
  username,
  entityId,
  entityRole,
}: UseBidirectionalChatProps) {
  const queryClient = useQueryClient();
  const [channel, setChannel] = useState<ReturnType<
    typeof supabase.channel
  > | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const previousRoomNameRef = useRef<string>('');
  const isLoadingRef = useRef(false);
  const channelNameRef = useRef<string>('');
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const healthCheckIntervalRef = useRef<NodeJS.Timeout>();
  const typingTimeoutRef = useRef<NodeJS.Timeout>();
  const lastConnectionAttemptRef = useRef<number>(0);
  const isConnectedRef = useRef(false); // Ref to track connection state (avoids closure issues)
  const MESSAGES_PER_PAGE = 50;
  const [oldestMessageTimestamp, setOldestMessageTimestamp] = useState<string | null>(null);
  const [messageOffset, setMessageOffset] = useState(0);

  // Query key for this room's messages - used for caching
  const messagesQueryKey = ['messages', roomName] as const;

  // Helper to update messages in React Query cache
  const setMessages = useCallback((updater: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => {
    queryClient.setQueryData<ChatMessage[]>(messagesQueryKey, (old = []) => {
      if (typeof updater === 'function') {
        return updater(old);
      }
      return updater;
    });
  }, [queryClient, roomName]);

  // Fetch messages function - used by React Query for initial load
  const fetchMessages = useCallback(async (): Promise<ChatMessage[]> => {
    if (!roomName) return [];

    // Clear expired cache on load
    clearExpiredCache();

    const options = {
      limit: MESSAGES_PER_PAGE,
      order: 'desc' as const,
    };

    const data = await messagingApi.listMessages(roomName, options) as any[];

    if (!data || data.length === 0) {
      setHasMoreMessages(false);
      setMessageOffset(0);
      setOldestMessageTimestamp(null);
      return [];
    }

    // Reverse to show oldest first in the UI
    const reversedData = [...data].reverse();

    // Update the oldest timestamp for next pagination
    if (reversedData.length > 0) {
      setOldestMessageTimestamp(reversedData[0].created_at);
    }

    const formattedMessages: ChatMessage[] = reversedData.map((msg) => ({
      id: msg.id,
      content: msg.content,
      user: {
        id: msg.user_id,
        name: msg.user_name,
      },
      createdAt: msg.created_at,
      entityId: msg.entity_id,
      entityRole: msg.entity_role,
      status: msg.status || 'sent',
      hasAttachments: msg.has_attachments || false,
      attachmentCount: msg.attachment_count || 0,
      messageType: msg.message_type || 'text',
      attachments: msg.attachments || [],
      editedAt: msg.edited_at || null,
      deletedAt: msg.deleted_at || null,
      originalContent: msg.original_content || null,
      reactions: [],
      isPinned: false,
    }));

    // Bulk fetch attachments for messages that need them
    const messagesNeedingAttachments = formattedMessages.filter(msg =>
      msg.hasAttachments && msg.attachmentCount > 0 && (!msg.attachments || msg.attachments.length === 0)
    );

    if (messagesNeedingAttachments.length > 0) {
      devLog(`📦 BULK FETCH: ${messagesNeedingAttachments.length} messages need attachments`);

      const messageIds = messagesNeedingAttachments.map(m => m.id);
      const { cached, missing } = getBulkCachedAttachments(messageIds);

      formattedMessages.forEach(msg => {
        if (cached[msg.id]) {
          msg.attachments = cached[msg.id];
        }
      });

      if (missing.length > 0) {
        devLog(`🌐 FETCHING: ${missing.length} messages from API`);
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            const response = await fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/message-attachments?room_name=${encodeURIComponent(roomName)}`,
              { headers: { Authorization: `Bearer ${session.access_token}` } }
            );

            if (response.ok) {
              const attachmentsByMessage = await response.json();
              cacheBulkAttachments(attachmentsByMessage);
              formattedMessages.forEach(msg => {
                if (attachmentsByMessage[msg.id]) {
                  msg.attachments = attachmentsByMessage[msg.id];
                }
              });
            }
          }
        } catch (error) {
          devError('Error bulk fetching attachments:', error);
        }
      }
    }

    // Fetch reactions for all messages
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const messageIds = formattedMessages.map(m => m.id);
        const reactionsPromises = messageIds.map(async (msgId) => {
          try {
            const response = await fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/messages/${msgId}/reactions`,
              { headers: { Authorization: `Bearer ${session.access_token}` } }
            );
            if (response.ok) {
              const reactions = await response.json();
              return { msgId, reactions };
            }
          } catch { /* Ignore individual failures */ }
          return { msgId, reactions: [] };
        });

        const reactionsResults = await Promise.all(reactionsPromises);
        reactionsResults.forEach(({ msgId, reactions }) => {
          const msg = formattedMessages.find(m => m.id === msgId);
          if (msg && reactions.length > 0) {
            msg.reactions = reactions.map((r: any) => ({
              id: r.id,
              emoji: r.emoji,
              entityId: r.entity_id,
              entityRole: r.entity_role,
              createdAt: r.created_at,
            }));
          }
        });
      }
    } catch (error) {
      devError('Error fetching reactions:', error);
    }

    // Fetch pinned messages for the room
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/messages/pinned?room_name=${encodeURIComponent(roomName)}`,
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );
        if (response.ok) {
          const pinnedMessages = await response.json();
          const pinnedMessageIds = new Set(pinnedMessages.map((pm: any) => pm.messages?.id).filter(Boolean));
          formattedMessages.forEach(msg => {
            if (pinnedMessageIds.has(msg.id)) {
              msg.isPinned = true;
            }
          });
          devLog(`📌 Found ${pinnedMessageIds.size} pinned messages`);
        }
      }
    } catch (error) {
      devError('Error fetching pinned messages:', error);
    }

    // Update pagination state
    setMessageOffset(data.length);
    setHasMoreMessages(data.length >= MESSAGES_PER_PAGE);

    return formattedMessages;
  }, [roomName]);

  // React Query for messages - provides caching and instant display
  const {
    data: messages = [],
    isLoading: isLoadingMessages,
    refetch: refetchMessages,
  } = useQuery({
    queryKey: messagesQueryKey,
    queryFn: fetchMessages,
    enabled: !!roomName,
    staleTime: 2 * 60 * 1000, // Consider fresh for 2 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch if we have cached data
  });

  // Log initialization
  useEffect(() => {
    devLog('🎬 Hook initialized:', {
      roomName,
      username,
      entityId,
      entityRole,
      timestamp: new Date().toISOString()
    });
  }, []);

  // Track connection state changes AND sync the ref
  useEffect(() => {
    devLog('🔌 CONNECTION STATE CHANGED:', {
      isConnected,
      isReconnecting,
      roomName,
      channelExists: !!channel,
      timestamp: new Date().toISOString()
    });
    // Keep the ref in sync with state (to avoid stale closure issues in useEffect callbacks)
    isConnectedRef.current = isConnected;
  }, [isConnected, isReconnecting]);

  // Add a reconnection function with exponential backoff
  const attemptReconnect = useCallback(async () => {
    // Debounce rapid reconnection attempts
    const now = Date.now();
    if (now - lastConnectionAttemptRef.current < 2000) {
      devLog('⏸️ Skipping reconnect - too soon since last attempt');
      return;
    }
    lastConnectionAttemptRef.current = now;

    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      devError('❌ Max reconnection attempts reached');
      setIsReconnecting(false);
      return;
    }

    reconnectAttemptsRef.current++;
    const backoffDelay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 10000);

    devLog(`🔄 Attempting reconnection ${reconnectAttemptsRef.current}/${maxReconnectAttempts} in ${backoffDelay}ms`);
    setIsReconnecting(true);

    // Clean up existing channel first using connection manager
    if (channelNameRef.current) {
      try {
        await connectionManager.removeChannel(channelNameRef.current);
      } catch (error) {
        devError('Error removing channel during reconnect:', error);
      }
    }

    // Schedule reconnection with backoff
    reconnectTimeoutRef.current = setTimeout(() => {
      setChannel(null);
      // Note: Don't set isConnected(false) here - it causes a race condition
      // where this timeout fires after a successful reconnection, incorrectly
      // disabling the chat input. The connection state will be updated by
      // the subscription callback when the new channel connects.
    }, backoffDelay);
  }, []);

  // Reset reconnect attempts when successfully connected
  useEffect(() => {
    if (isConnected) {
      reconnectAttemptsRef.current = 0;
      setIsReconnecting(false);
    }
  }, [isConnected]);

  // Load existing messages from database (with pagination)
  const loadMessages = useCallback(
    async (chatRoomName: string, loadMore = false) => {
      if (isLoadingRef.current || (!hasMoreMessages && loadMore)) return;
      isLoadingRef.current = true;
      if (loadMore) setIsLoadingMore(true);

      try {
        // Clear expired cache on first load
        if (!loadMore) {
          clearExpiredCache();
        }

        // For pagination, use timestamp-based cursor or offset
        const options: any = {
          limit: MESSAGES_PER_PAGE,
          order: 'desc' as const  // Get newest messages first
        };

        // When loading more, use either the oldest message timestamp or offset
        if (loadMore) {
          if (oldestMessageTimestamp) {
            options.before = oldestMessageTimestamp;
          } else {
            options.offset = messageOffset;
          }
        }

        const data = await messagingApi.listMessages(chatRoomName, options) as any[];

        if (data && data.length > 0) {
          // Reverse to show oldest first in the UI
          const reversedData = [...data].reverse();

          // Update the oldest timestamp for next pagination
          if (reversedData.length > 0) {
            setOldestMessageTimestamp(reversedData[0].created_at);
          }

          const formattedMessages: ChatMessage[] = reversedData.map((msg) => {
            const formatted: ChatMessage = {
              id: msg.id,
              content: msg.content,
              user: {
                id: msg.user_id,
                name: msg.user_name,
              },
              createdAt: msg.created_at,
              entityId: msg.entity_id,
              entityRole: msg.entity_role,
              status: msg.status || 'sent',
              hasAttachments: msg.has_attachments || false,
              attachmentCount: msg.attachment_count || 0,
              messageType: msg.message_type || 'text',
              attachments: msg.attachments || [], // Include if already provided
              // Edit/delete fields
              editedAt: msg.edited_at || null,
              deletedAt: msg.deleted_at || null,
              originalContent: msg.original_content || null,
              // Will be populated below
              reactions: [],
              isPinned: false,
            };

            return formatted;
          });

          // 🔥 BULK FETCH ATTACHMENTS FOR MESSAGES THAT NEED THEM
          const messagesNeedingAttachments = formattedMessages.filter(msg =>
            msg.hasAttachments && msg.attachmentCount > 0 && (!msg.attachments || msg.attachments.length === 0)
          );

          if (messagesNeedingAttachments.length > 0) {
            devLog(`📦 BULK FETCH: ${messagesNeedingAttachments.length} messages need attachments`);

            const messageIds = messagesNeedingAttachments.map(m => m.id);

            // Check cache first
            const { cached, missing } = getBulkCachedAttachments(messageIds);

            // Apply cached attachments
            formattedMessages.forEach(msg => {
              if (cached[msg.id]) {
                msg.attachments = cached[msg.id];
              }
            });

            // Bulk fetch missing attachments
            if (missing.length > 0) {
              devLog(`🌐 FETCHING: ${missing.length} messages from API`);
              try {
                // Fetch attachments for room (most efficient on initial load)
                const { data: { session } } = await supabase.auth.getSession();
                if (session) {
                  const response = await fetch(
                    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/message-attachments?room_name=${encodeURIComponent(chatRoomName)}`,
                    {
                      headers: {
                        Authorization: `Bearer ${session.access_token}`,
                      },
                    }
                  );

                  if (response.ok) {
                    const attachmentsByMessage = await response.json();

                    // Cache all fetched attachments
                    cacheBulkAttachments(attachmentsByMessage);

                    // Apply to messages
                    formattedMessages.forEach(msg => {
                      if (attachmentsByMessage[msg.id]) {
                        msg.attachments = attachmentsByMessage[msg.id];
                      }
                    });

                    devLog(`💾 Cached attachments for ${Object.keys(attachmentsByMessage).length} messages`);
                  }
                }
              } catch (error) {
                devError('Error bulk fetching attachments:', error);
                // Don't fail the whole load if attachments fail
              }
            }
          }

          // 🔥 FETCH REACTIONS FOR ALL MESSAGES
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              // Fetch reactions for each message in parallel
              const messageIds = formattedMessages.map(m => m.id);
              const reactionsPromises = messageIds.map(async (msgId) => {
                try {
                  const response = await fetch(
                    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/messages/${msgId}/reactions`,
                    {
                      headers: {
                        Authorization: `Bearer ${session.access_token}`,
                      },
                    }
                  );
                  if (response.ok) {
                    const reactions = await response.json();
                    return { msgId, reactions };
                  }
                } catch {
                  // Ignore individual failures
                }
                return { msgId, reactions: [] };
              });

              const reactionsResults = await Promise.all(reactionsPromises);
              reactionsResults.forEach(({ msgId, reactions }) => {
                const msg = formattedMessages.find(m => m.id === msgId);
                if (msg && reactions.length > 0) {
                  msg.reactions = reactions.map((r: any) => ({
                    id: r.id,
                    emoji: r.emoji,
                    entityId: r.entity_id,
                    entityRole: r.entity_role,
                    createdAt: r.created_at,
                  }));
                }
              });
            }
          } catch (error) {
            devError('Error fetching reactions:', error);
          }

          // 🔥 FETCH PINNED MESSAGES FOR THE ROOM
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              const response = await fetch(
                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/messages/pinned?room_name=${encodeURIComponent(chatRoomName)}`,
                {
                  headers: {
                    Authorization: `Bearer ${session.access_token}`,
                  },
                }
              );
              if (response.ok) {
                const pinnedMessages = await response.json();
                const pinnedMessageIds = new Set(pinnedMessages.map((pm: any) => pm.messages?.id).filter(Boolean));
                formattedMessages.forEach(msg => {
                  if (pinnedMessageIds.has(msg.id)) {
                    msg.isPinned = true;
                  }
                });
                devLog(`📌 Found ${pinnedMessageIds.size} pinned messages`);
              }
            }
          } catch (error) {
            devError('Error fetching pinned messages:', error);
          }

          if (loadMore) {
            // Prepend older messages to the beginning
            setMessages(prev => [...formattedMessages, ...prev]);
            // Update offset for next load
            setMessageOffset(prev => prev + data.length);
          } else {
            // Initial load - reset pagination state
            setMessages(formattedMessages);
            setMessageOffset(data.length);
            setHasMoreMessages(true);
          }

          // Check if there are more messages
          if (data.length < MESSAGES_PER_PAGE) {
            setHasMoreMessages(false);
          }
        } else {
          if (!loadMore) {
            setMessages([]);
            setMessageOffset(0);
            setOldestMessageTimestamp(null);
          }
          setHasMoreMessages(false);
        }
      } catch (error) {
        devError('Error loading messages:', error);
        // Clear messages on error
        if (!loadMore) {
          setMessages([]);
          setMessageOffset(0);
          setOldestMessageTimestamp(null);
        }
      } finally {
        isLoadingRef.current = false;
        setIsLoadingMore(false);
      }
    },
    [hasMoreMessages, oldestMessageTimestamp, messageOffset]
  );

  // --------------------------------------------
  // Setup: heartbeat tuning for background tabs
  // --------------------------------------------
  useEffect(() => {
    try {
      // Reduce interval to 15 s so three missed beats < 1 min (Chrome throttle)
      (supabase.realtime as any)?.setHeartbeatInterval?.(15000);
    } catch (_) {/* noop */}
  }, []);

  // --------------------------------------------------
  // Reconnect when tab becomes visible / window focus
  // --------------------------------------------------
  useEffect(() => {
    const handleResume = () => {
      if (!document.hidden && !isConnected && !isReconnecting) {
        devLog('👀 Tab active – triggering reconnect');
        attemptReconnect();
      }
    };
    window.addEventListener('visibilitychange', handleResume);
    window.addEventListener('focus', handleResume);
    return () => {
      window.removeEventListener('visibilitychange', handleResume);
      window.removeEventListener('focus', handleResume);
    };
  }, [isConnected, isReconnecting, attemptReconnect]);

  // Clear messages immediately when roomName changes
  useEffect(() => {
    // Clear messages when switching rooms (but not on initial mount)
    if (previousRoomNameRef.current && previousRoomNameRef.current !== roomName) {
      // Clean up existing channel when switching rooms
      if (channel && previousRoomNameRef.current) {
        connectionManager.removeChannel(previousRoomNameRef.current).catch(err => {
          devError('Error removing channel on room switch:', err);
        });
      }

      setChannel(null);
      setMessages([]);
      setIsConnected(false);
      // Reset pagination state
      setHasMoreMessages(true);
      setOldestMessageTimestamp(null);
      setMessageOffset(0);
    }

    // Also clear if roomName becomes empty
    if (!roomName && previousRoomNameRef.current) {
      setMessages([]);
      setIsConnected(false);
      // Reset pagination state
      setHasMoreMessages(true);
      setOldestMessageTimestamp(null);
      setMessageOffset(0);
    }
  }, [roomName]);

  // Set up realtime channel
  useEffect(() => {
    let newChannel: ReturnType<typeof supabase.channel> | null = null;
    let isMounted = true;
    let setupTimeout: NodeJS.Timeout | null = null;

    const setupChannel = async () => {
      devLog('📞 setupChannel called:', {
        roomName,
        hasRoomName: !!roomName,
        isConnected,
        previousRoomName: previousRoomNameRef.current,
        channelExists: !!channel,
        isMounted,
        timestamp: new Date().toISOString()
      });

      if (!roomName) {
        devWarn('❌ No room name provided for chat - setting isConnected=false');
        setIsConnected(false);
        setMessages([]);
        return;
      }

      // If already connected to this room AND channel exists, don't reconnect
      // Use isConnectedRef.current to avoid stale closure issues when useEffect re-runs
      if (isConnectedRef.current && previousRoomNameRef.current === roomName && channel) {
        devLog('🔄 Already connected to room, skipping reconnect (ref check)');
        return;
      }

      // Store current room name for reference
      previousRoomNameRef.current = roomName;
      devLog('📝 Stored room name, React Query will handle initial message load');

      // Note: React Query handles initial message loading automatically via fetchMessages
      // No need to call loadMessages here - it runs when roomName is set

      // Clean up any existing channel before creating a new one
      if (channel && previousRoomNameRef.current) {
        try {
          await connectionManager.removeChannel(previousRoomNameRef.current);
          // Reset state after cleanup
          if (isMounted) {
            setChannel(null);
            setIsConnected(false);
          }
        } catch (error) {
          devError('Error removing previous channel:', error);
        }
      }

      if (!isMounted) return;

      // Create a consistent channel name based on the room name
      // IMPORTANT: Must match mobile channel name format for cross-platform typing indicators
      let channelName = `room:${roomName}`;

      // If channel name is too long (over 100 chars), use a shorter version
      if (channelName.length > 100) {
        // Use just the last part of the room name (should be unique enough)
        const parts = roomName.split('_');
        const shortName = `room:${parts[parts.length - 2]}_${parts[parts.length - 1]}`;
        devLog('⚠️ Channel name too long, using shortened version:', { original: channelName, shortened: shortName });
        channelName = shortName;
      }

      channelNameRef.current = channelName;
      devLog('🚀 Setting up channel:', { channelName, roomName, entityId, entityRole, isMounted });

      // Check auth status
      devLog('🔐 Checking auth status...');
      const { data: { session }, error: authError } = await supabase.auth.getSession();
      devLog('🔐 Auth result:', {
        hasSession: !!session,
        hasError: !!authError,
        errorMessage: authError?.message,
        userEmail: session?.user?.email,
        isMounted
      });
      if (authError || !session) {
        devError('❌ Auth check failed - setting isConnected=false:', authError || 'No session');
        setIsConnected(false);
        return;
      }
      devLog('✅ Auth verified, user:', session.user.email);

      // Get connection stats for monitoring
      const stats = connectionManager.getStats();
      devLog('📊 Connection Manager Stats:', stats);

      // Use connection manager to get or create channel with proper config
      devLog('📡 Getting channel from connection manager...');
      const channelConfig = {
        config: {
          broadcast: {
            self: true, // Must be true for bidirectional chat
            ack: true, // Request acknowledgment for reliable delivery
          },
          presence: { key: username },
        },
      };
      const managedChannel = connectionManager.getChannel(channelName, channelConfig);
      devLog('📡 Connection manager returned:', {
        hasChannel: !!managedChannel,
        channelState: managedChannel?.state,
        channelTopic: managedChannel?.topic,
        isMounted
      });

      if (!managedChannel) {
        devError('❌ Failed to get channel from connection manager - setting isConnected=false');
        setIsConnected(false);
        return;
      }

      // Check if it's an existing channel that's already joined
      if (managedChannel.state === 'joined') {
        devLog('♻️ Reusing existing joined channel - setting isConnected=true');
        setChannel(managedChannel);
        setIsConnected(true);
        isConnectedRef.current = true; // Update ref immediately
        return;
      }

      newChannel = managedChannel;

      try {
        devLog('📝 Channel obtained from manager:', {
          state: managedChannel.state,
          topic: managedChannel.topic,
          params: managedChannel.params,
          socket: managedChannel.socket,
          socketState: managedChannel.socket?.state,
          socketIsConnected: managedChannel.socket?.isConnected?.(),
          stats: connectionManager.getStats()
        });



        // If the channel is already joined (e.g. reused from cache) treat as connected
        if (managedChannel.state === 'joined') {
          devLog('⚡ Channel already joined, no subscribe needed');
          setIsConnected(true);
          isConnectedRef.current = true; // Update ref immediately
          setChannel(managedChannel);

          // Still need to attach health-check interval
          healthCheckIntervalRef.current = setInterval(() => {
            const channelName = channelNameRef.current;
            if (channelName && !connectionManager.isChannelHealthy(channelName)) {
              devWarn('⚠️ Channel health check failed – connection lost');
              clearInterval(healthCheckIntervalRef.current);
              setIsConnected(false);
              attemptReconnect();
            }
          }, 30000);

          return;
        }

        // Set a timeout for connection
        devLog('⏰ Setting 10s connection timeout...');
        const connectionTimeout = setTimeout(() => {
          devError('⏱️ CONNECTION TIMEOUT FIRED after 10s:', {
            isConnected,
            isMounted,
            channelState: newChannel?.state,
            channelTopic: newChannel?.topic,
            timestamp: new Date().toISOString()
          });
          if (!isConnected && isMounted) {
            devError('⏱️ Timeout condition met - setting isConnected=false, attempting reconnect');
            setIsConnected(false);
            setChannel(null);
            // Try reconnect after timeout
            attemptReconnect();
          } else {
            devLog('⏰ Timeout fired but already connected or unmounted, ignoring');
          }
        }, 10000); // 10 second timeout

        // Add event listeners BEFORE subscribing
        devLog('🔄 Setting up event listeners...');

        // First add all event listeners
        newChannel
          .on('presence', { event: 'sync' }, () => {
            // Keep-alive presence sync to prevent channel from closing
            devLog('👥 Presence synced');

            // Extract typing users from presence state
            const state = newChannel.presenceState();
            const typing: TypingUser[] = [];

            Object.values(state).forEach((presences: any[]) => {
              presences.forEach((presence: any) => {
                // Only show typing for other users
                if (presence.typing && presence.entity_id !== entityId) {
                  typing.push({
                    id: presence.entity_id,
                    name: presence.user_name || presence.user || 'Someone',
                    role: presence.entity_role || 'user',
                  });
                }
              });
            });

            setTypingUsers(typing);
          })
          .on('presence', { event: 'join' }, (payload) => {
            devLog('👤 User joined:', payload);
          })
          .on('presence', { event: 'leave' }, (payload) => {
            devLog('👤 User left:', payload);
            // Clear typing status for user who left
            const leftUser = payload.leftPresences?.[0];
            if (leftUser?.entity_id) {
              setTypingUsers(prev => prev.filter(u => u.id !== leftUser.entity_id));
            }
          })
          .on('broadcast', { event: EVENT_MESSAGE_TYPE }, (payload) => {
            try {
              const rawMessage = payload.payload as any;

              // Convert message to ChatMessage format
              // Handle both camelCase (from web) and snake_case (from mobile) formats
              const newMessage: ChatMessage = {
                id: rawMessage.id,
                content: rawMessage.content,
                createdAt: rawMessage.createdAt || rawMessage.created_at || new Date().toISOString(),
                user: rawMessage.user || {
                  id: rawMessage.user_id || rawMessage.entityId || '',
                  name: rawMessage.user_name || 'Unknown',
                },
                entityId: rawMessage.entityId || rawMessage.entity_id,
                entityRole: rawMessage.entityRole || rawMessage.entity_role,
                status: rawMessage.status,
                hasAttachments: rawMessage.hasAttachments || rawMessage.has_attachments || false,
                attachmentCount: rawMessage.attachmentCount || rawMessage.attachment_count || 0,
                messageType: rawMessage.messageType || rawMessage.message_type,
                attachments: rawMessage.attachments,
              };

              // Add to messages if not already present
              if (isMounted) {
                setMessages((current) => {
                  // Check if message already exists
                  const existingIndex = current.findIndex((msg) => msg.id === newMessage.id);
                  if (existingIndex !== -1) {
                    // Message exists - preserve attachment fields from existing message
                    const existing = current[existingIndex];
                    const updated = [...current];
                    updated[existingIndex] = {
                      ...newMessage,
                      hasAttachments: existing.hasAttachments ?? newMessage.hasAttachments,
                      attachmentCount: existing.attachmentCount ?? newMessage.attachmentCount,
                      messageType: existing.messageType ?? newMessage.messageType,
                    };
                    return updated;
                  }
                  return [...current, newMessage];
                });

                // Update message status to delivered when receiving a message from the other user
                if (newMessage.entityId !== entityId) {
                  // OPTIMISTIC: Broadcast status update immediately (non-blocking)
                  if (channel) {
                    channel
                      .send({
                        type: 'broadcast',
                        event: EVENT_STATUS_TYPE,
                        payload: {
                          messageId: newMessage.id,
                          status: 'delivered',
                          entityId: newMessage.entityId,
                          updatedBy: entityId,
                        },
                      })
                      .catch((err) => {
                        devError(
                          `Error broadcasting delivered status:`,
                          err
                        );
                      });
                  }

                  // Store in database in background (don't block)
                  storeAllMessageStatus(
                    roomName,
                    newMessage.entityId || '',
                    'delivered'
                  ).catch((err) => {
                    devError(
                      'Error updating message status to delivered:',
                      err
                    );
                  });
                }
              }
            } catch (error) {
              devError('Error processing received message:', error);
            }
          })
          .on('broadcast', { event: EVENT_STATUS_TYPE }, (payload) => {
            try {
              const statusUpdate = payload.payload as {
                messageId: string;
                status: 'sent' | 'delivered' | 'read';
                entityId: string;
                updatedBy: string;
              };

              // Only process status updates for our messages
              if (statusUpdate.entityId === entityId) {
                // Update the status of the message in the local state
                setMessages((currentMessages) => {
                  const updatedMessages = currentMessages.map((msg) => {
                    if (
                      msg.id === statusUpdate.messageId ||
                      // Also update older messages to the same status
                      (msg.entityId === statusUpdate.entityId &&
                        msg.createdAt <= statusUpdate.messageId &&
                        (!msg.status ||
                          (msg.status === 'sent' &&
                            statusUpdate.status === 'delivered') ||
                          (msg.status !== 'read' &&
                            statusUpdate.status === 'read')))
                    ) {
                      // Store this status change to the database to ensure it persists
                      storeMessageStatus(msg.id, statusUpdate.status);

                      return {
                        ...msg,
                        status: statusUpdate.status,
                      };
                    }
                    return msg;
                  });

                  return updatedMessages;
                });
              }
            } catch (error) {
              devError('Error processing status update:', error);
            }
          });

        // Track presence to keep channel alive
        const presenceKey = `${entityRole}_${entityId}`;

        // Now subscribe to the channel with presence
        devLog('🔄 About to call subscribe on channel...', {
          channelTopic: newChannel.topic,
          channelState: newChannel.state,
          channelSocket: newChannel.socket?.state,
          isClosed: newChannel.state === 'closed',
          isErrored: newChannel.state === 'errored',
          presenceKey
        });

        const subscribeResult = newChannel.subscribe(async (status, error) => {
          devLog('📡 SUBSCRIPTION CALLBACK FIRED:', {
            status,
            error: error?.message || error,
            roomName,
            entityId,
            channelName,
            channelState: newChannel?.state,
            isMounted,
            timestamp: new Date().toISOString()
          });

          // Handle different subscription states
          if (status === 'SUBSCRIBED' && isMounted) {
            devLog('✅✅✅ CHANNEL SUBSCRIBED SUCCESSFULLY - setting isConnected=true');
            clearTimeout(connectionTimeout); // Clear the timeout
            setIsConnected(true);
            isConnectedRef.current = true; // Update ref immediately
            setChannel(newChannel);

            // Track presence to keep connection alive
            // IMPORTANT: Include user_name and typing for cross-platform compatibility with mobile
            try {
              await newChannel.track({
                user: presenceKey,
                user_name: username,
                entity_id: entityId,
                entity_role: entityRole,
                typing: false,
                online_at: new Date().toISOString(),
              });
              devLog('👥 Presence tracked successfully');
            } catch (err) {
              devError('Failed to track presence:', err);
            }

            // Set up health check monitoring
            healthCheckIntervalRef.current = setInterval(() => {
              const channelName = channelNameRef.current;

              if (channelName && !connectionManager.isChannelHealthy(channelName)) {
                devWarn('⚠️ Channel health check failed - connection lost');
                clearInterval(healthCheckIntervalRef.current);
                setIsConnected(false);
                attemptReconnect();
              }
            }, 30000); // Check every 30 seconds

            // Mark messages as read when user opens chat
            // This happens when user becomes present in the channel
            storeAllMessageStatus(
              roomName,
              entityId === 'client' ? 'filmmaker' : 'client',
              'read'
            )
              .then(() => {
                // Broadcast read status to sender
                if (newChannel) {
                  newChannel.send({
                    type: 'broadcast',
                    event: EVENT_STATUS_TYPE,
                    payload: {
                      status: 'read',
                      room: roomName,
                      recipientRole: entityRole,
                      recipientId: entityId,
                    },
                  });
                }
              })
              .catch((err) =>
                devError('Error marking messages as read:', err)
              );
          } else if (status === 'CHANNEL_ERROR' && isMounted) {
            devLog('❌ Channel subscription failed with CHANNEL_ERROR');
            // Try to get more error details
            devError('Channel error details:', { status, error, channelName, roomName });
            setIsConnected(false);
            setChannel(null);

            // If we hit connection limit, use connection manager to cleanup
            if (error?.message?.includes('DatabaseLackOfConnections') ||
                error?.message?.includes('Database can\'t accept more connections')) {
              devError('🚨 DATABASE CONNECTION LIMIT HIT - Cleaning up ALL channels');

              // Use connection manager's cleanup method which handles everything
              // It will remove ALL Supabase channels (even untracked ones) and reset its state
              await connectionManager.cleanup();

              devLog('All channels cleaned up via connection manager, waiting before retry...');

              // Clear timeout to prevent multiple retries
              clearTimeout(connectionTimeout);

              // Wait longer before retrying, especially on mobile
              const retryDelay = isMobileDevice ? 10000 : 5000;
              setTimeout(() => {
                if (isMounted && !isConnected) {
                  devLog('Retrying after connection limit cleanup...');
                  attemptReconnect();
                }
              }, retryDelay);
            } else {
              // Regular CHANNEL_ERROR handling
              if (newChannel && channelNameRef.current) {
                try {
                  await connectionManager.removeChannel(channelNameRef.current);
                } catch (err) {
                  devError('Error removing failed channel:', err);
                }
              }

              // Clear timeout to prevent multiple retries
              clearTimeout(connectionTimeout);

              // Attempt reconnect with backoff
              setTimeout(() => {
                if (isMounted && !isConnected) {
                  attemptReconnect();
                }
              }, 5000);
            }
          } else if (isMounted) {
            devLog('❌ Channel subscription failed:', status);
            setIsConnected(false);
          }
        });

        // Log the subscribe result
        devLog('📌 Subscribe method returned:', {
          subscribeResult,
          channelStateAfterSubscribe: newChannel.state,
          channelTopicAfterSubscribe: newChannel.topic
        });

        // Give subscribe a chance to connect
        devLog('⏳ Waiting for channel subscription...');
      } catch (error) {
        devError('❌ Error setting up channel:', error);
        if (isMounted) {
          setIsConnected(false);
          // Clear the channel reference so reconnection can be attempted
          setChannel(null);
        }
      }
    };

    // Add a delay to prevent rapid mount/unmount issues (especially with StrictMode)
    // This gives React time to settle if there are quick re-renders
    // Use longer delay on mobile devices to handle slower renders
    const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const setupDelay = isMobileDevice ? 1000 : 300; // Longer delay for mobile
    devLog('🕐 Scheduling setupChannel with delay:', { setupDelay, isMobileDevice, roomName });

    setupTimeout = setTimeout(() => {
      devLog('🕐 Setup timeout fired, isMounted:', isMounted, 'roomName:', roomName);
      if (isMounted) {
        setupChannel();
      } else {
        devLog('🕐 Component unmounted before setup, skipping');
      }
    }, setupDelay);

    // Cleanup function
    return () => {
      devLog('🧹 CLEANUP FUNCTION CALLED:', {
        roomName,
        channelName: channelNameRef.current,
        wasConnected: isConnected,
        timestamp: new Date().toISOString()
      });
      isMounted = false;

      // Clear setup timeout if it exists
      if (setupTimeout) {
        devLog('🧹 Clearing setup timeout');
        clearTimeout(setupTimeout);
      }

      // Clear all timers
      if (reconnectTimeoutRef.current) {
        devLog('🧹 Clearing reconnect timeout');
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (healthCheckIntervalRef.current) {
        devLog('🧹 Clearing health check interval');
        clearInterval(healthCheckIntervalRef.current);
      }

      // Schedule channel cleanup on unmount (with grace period for quick reopens)
      // The connection manager will handle reuse if the same channel is requested again quickly
      if (channelNameRef.current) {
        const channelToClean = channelNameRef.current;
        devLog('🧹 Scheduling channel cleanup on unmount:', channelToClean);
        connectionManager.scheduleChannelCleanup(channelToClean);
      }

      devLog('🧹 Setting isConnected=false in cleanup (state + ref)');
      setIsConnected(false);
      isConnectedRef.current = false; // Update ref immediately (state update is async)
    };
  }, [roomName, username, entityId, entityRole, loadMessages, queryClient]);

  // Send message function with optional attachment metadata and messageId
  const sendMessage = useCallback(
    async (content: string, attachmentInfo?: { hasAttachments?: boolean; attachmentCount?: number; messageType?: 'text' | 'media' | 'mixed' | 'voice' | 'location'; messageId?: string }) => {
      devLog('💬 sendMessage called:', {
        hasContent: !!content.trim(),
        hasAttachments: attachmentInfo?.hasAttachments,
        isConnected,
        hasChannel: !!channel,
        roomName
      });

      // Allow empty content if there are attachments (media-only messages)
      if (!content.trim() && !attachmentInfo?.hasAttachments) {
        devLog('💬 Empty message, skipping');
        return;
      }

      // Check connection status
      if (!channel || !isConnected) {
        devWarn('⚠️ CANNOT SEND MESSAGE - NOT CONNECTED:', {
          hasChannel: !!channel,
          isConnected,
          isReconnecting,
          roomName
        });
        attemptReconnect();
        return;
      }

      try {
        // Use provided message ID or generate a new one
        const messageId = attachmentInfo?.messageId || crypto.randomUUID();

        // Use provided entity information
        if (!entityId) {
          devError('Cannot send message: no entity ID provided');
          return;
        }

        // Create message object with entity information (use cached user ID for speed)
        const message: ChatMessage = {
          id: messageId,
          content,
          user: {
            id: entityId, // Use entity ID directly for instant display
            name: username,
          },
          createdAt: new Date().toISOString(),
          entityId,
          entityRole,
          status: 'sent',
          hasAttachments: attachmentInfo?.hasAttachments,
          attachmentCount: attachmentInfo?.attachmentCount,
          messageType: attachmentInfo?.messageType,
        };

        // OPTIMISTIC UPDATE: Add message to local state IMMEDIATELY for instant UX
        setMessages((current) => [...current, message]);

        // Store in database in background (don't block UI)
        (async () => {
          try {
            // Get auth user ID in background
            const { data } = await supabase.auth.getUser();
            const authUserId = data?.user?.id || 'anonymous';

            const messagePayload = {
              id: messageId,
              content: message.content,
              user_id: authUserId,
              user_name: message.user.name,
              room_name: roomName,
              created_at: message.createdAt,
              entity_id: entityId,
              entity_role: entityRole,
              status: message.status,
              has_attachments: attachmentInfo?.hasAttachments,
              attachment_count: attachmentInfo?.attachmentCount,
              message_type: attachmentInfo?.messageType,
            };

            // Debug logging for voice notes
            if (attachmentInfo?.messageType === 'voice') {
              console.log('📣 Sending voice message with payload:', messagePayload);
            }

            await messagingApi.sendMessage(messagePayload);
          } catch (dbErr) {
            devError('Error storing message:', dbErr);
            // Message already shown optimistically - could mark as failed here
          }

          // Send email notification to recipient (non-blocking)
          try {
            await sendMessageEmailNotification(
              content,
              roomName,
              entityId,
              entityRole,
              username
            );
          } catch (emailError) {
            devError('Failed to send message email notification:', emailError);
          }
        })();

        // Broadcast to channel (non-blocking for instant UX)
        channel.send({
          type: 'broadcast',
          event: EVENT_MESSAGE_TYPE,
          payload: message,
        }).catch((error) => {
          devError('Error broadcasting message:', error);
        });

        // Invalidate conversations query to update the conversation list with new message
        queryClient.invalidateQueries({ queryKey: ['conversations'] });

        // Dispatch event to notify other components that a message was sent
        window.dispatchEvent(new CustomEvent('message-sent'));
      } catch (error) {
        devError('Error sending message:', error);
      }
    },
    [channel, isConnected, roomName, username, entityId, entityRole]
  );

  // Add a useEffect to mark messages as read when they appear in the viewport
  useEffect(() => {
    // Skip if no messages or no connection
    if (!messages.length || !isConnected || !channel) return;

    // Only process other user's messages that aren't read yet
    const unreadMessages = messages.filter(
      (msg) =>
        msg.entityId !== entityId && (!msg.status || msg.status !== 'read')
    );

    if (unreadMessages.length === 0) return;

    // Get the IDs of messages to mark as read (keeping for potential future use)
    // const messageIds = unreadMessages.map((msg) => msg.id);

    // For each unique sender, update all their messages
    const senderIds = [...new Set(unreadMessages.map((msg) => msg.entityId))];

    senderIds.forEach((senderId) => {
      if (!senderId) return;

      // OPTIMISTIC UPDATE: Update local state immediately
      setMessages((current) => {
        const updated = current.map((msg) =>
          msg.entityId === senderId &&
          (!msg.status || msg.status !== 'read')
            ? { ...msg, status: 'read' as const }
            : msg
        );
        return updated;
      });

      // Broadcast status update immediately (non-blocking)
      channel
        .send({
          type: 'broadcast',
          event: EVENT_STATUS_TYPE,
          payload: {
            messageId: new Date().toISOString(), // Use timestamp to ensure all messages up to now are marked
            status: 'read',
            entityId: senderId,
            updatedBy: entityId,
          },
        })
        .catch((err) => {
          devError(`Error broadcasting read status:`, err);
        });

      // Invalidate conversations query to update unread counts
      queryClient.invalidateQueries({ queryKey: ['conversations'] });

      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('messages-read'));

      // Store in database in background (don't block UI)
      storeAllMessageStatus(roomName, senderId, 'read')
        .then((result) => {
          if (!result.success) {
            devError(`Failed to update messages to 'read':`, result.error);
          }
        })
        .catch((error) => {
          devError('Error marking messages as read:', error);
        });
    });

    // Run this effect when messages change, but use a 2-second debounce
    // to avoid excessive database updates
  }, [messages, isConnected, channel, entityId, roomName]);

  // Function to load more messages (for infinite scroll)
  const loadMoreMessages = useCallback(() => {
    if (roomName && !isLoadingMore && hasMoreMessages) {
      loadMessages(roomName, true);
    }
  }, [roomName, isLoadingMore, hasMoreMessages, loadMessages]);

  // Function to broadcast typing status
  const setTyping = useCallback((isTyping: boolean) => {
    if (!channel || !isConnected) return;

    // Clear any existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Track presence with typing status
    channel.track({
      user: `${entityRole}_${entityId}`,
      user_name: username,
      entity_id: entityId,
      entity_role: entityRole,
      typing: isTyping,
      online_at: new Date().toISOString(),
    }).catch((err) => {
      devError('Failed to track typing status:', err);
    });

    // Auto-clear typing after 3 seconds if still typing
    if (isTyping) {
      typingTimeoutRef.current = setTimeout(() => {
        channel.track({
          user: `${entityRole}_${entityId}`,
          user_name: username,
          entity_id: entityId,
          entity_role: entityRole,
          typing: false,
          online_at: new Date().toISOString(),
        }).catch((err) => {
          devError('Failed to clear typing status:', err);
        });
      }, 3000);
    }
  }, [channel, isConnected, entityId, entityRole, username]);

  // Clean up typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Update a specific message (for reactions, pins, edits)
  const updateMessage = useCallback((messageId: string, updates: Partial<ChatMessage>) => {
    setMessages((current) =>
      current.map((msg) =>
        msg.id === messageId ? { ...msg, ...updates } : msg
      )
    );
  }, []);

  // Refresh messages from the server
  const refreshMessages = useCallback(() => {
    refetchMessages();
  }, [refetchMessages]);

  return {
    messages,
    sendMessage,
    isConnected,
    isReconnecting,
    isLoadingMessages, // Only true when no cached data exists
    loadMoreMessages,
    hasMoreMessages,
    isLoadingMore,
    typingUsers,
    setTyping,
    updateMessage,
    refreshMessages,
  };
}
